import React, { useState } from 'react';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FaArrowRight, FaBoxes, FaCheck, FaMapMarkedAlt, FaPencilRuler } from 'react-icons/fa';
import API_URL from '../config/api';
import { cloudinaryAsset } from '../config/media';

const asset = (folder, filename) => cloudinaryAsset(`/${folder}/${filename}`);

const projects = [
  {
    client: 'Axe',
    categoryKey: 'axe',
    folder: '2.1 Galería de proyectos/PDV - Axe',
    images: ['axe (1).png', 'axe (2).png'],
    serviceKeys: ['design', 'structure', 'graphics', 'installation']
  },
  {
    client: 'Grupo Sevel',
    categoryKey: 'sevel',
    folder: '2.1 Galería de proyectos/Stand - Grupo Sevel',
    images: ['Grupo Sevel (1).jpg', 'Grupo Sevel (2).jpg', 'Grupo Sevel (3).jpg', 'Grupo Sevel (4).jpg'],
    serviceKeys: ['spaceDesign', 'production', 'brandGraphics', 'venueSetup']
  },
  {
    client: 'Hubbard',
    categoryKey: 'hubbard',
    folder: '2.1 Galería de proyectos/Stand - Hubbard',
    images: ['Hubbard (1).jpg', 'Hubbard (2).jpg', 'Hubbard (3).jpg'],
    serviceKeys: ['customDevelopment', 'inHouseManufacturing', 'furnitureIntegration', 'logistics']
  },
  {
    client: 'INAC',
    categoryKey: 'inac',
    folder: '2.1 Galería de proyectos/Stand - INAC',
    images: ['INAC (1).png', 'INAC (2).png', 'INAC (3).png', '4dd2e2eb-007b-4a4a-9292-f7eb57b59082.png'],
    serviceKeys: ['concept', 'structures', 'lighting', 'installation']
  }
];

const standTypes = [
  {
    key: 'modular',
    image: asset('2.2 Tipos de stands/Modular', 'exhibirte (548).jpg'),
  },
  {
    key: 'custom',
    image: asset('2.2 Tipos de stands/A medida', 'b92c4d6e-c05a-4ddc-8c94-e88d0892458c.png'),
  },
  {
    key: 'outdoor',
    image: asset('2.2 Tipos de stands/Exteriores', 'Ramm.png'),
  },
  {
    key: 'portable',
    image: asset('2.2 Tipos de stands/Portátil', '685a2ed2-f936-49fb-857c-fa7cb69d0849.png'),
  }
];

const trustPoints = [
  { icon: FaPencilRuler, key: 'experience' },
  { icon: FaBoxes, key: 'projects' },
  { icon: FaCheck, key: 'manufacturing' },
  { icon: FaMapMarkedAlt, key: 'coverage' }
];

const CreacionStands = () => {
  const { t } = useTranslation();
  const [selectedProject, setSelectedProject] = useState(0);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', eventName: '', standSize: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const project = projects[selectedProject];

  const handleChange = ({ target: { name, value } }) => setFormData((current) => ({ ...current, [name]: value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/contactos`, {
        asunto: 'Stand design for events - website enquiry',
        nombre: formData.name,
        email: formData.email,
        telefono: formData.phone,
        mensaje: `${t('standsPage.form.eventName')}: ${formData.eventName}\n\n${formData.message}`
      });
      await Swal.fire({ icon: 'success', title: t('contactForm.alert_title'), text: t('contactForm.alert_success'), confirmButtonColor: '#1E5096' });
      setFormData({ name: '', email: '', phone: '', eventName: '', standSize: '', message: '' });
    } catch (error) {
      await Swal.fire({ icon: 'error', title: t('common.error'), text: t('standsPage.form.error'), confirmButtonColor: '#1E5096' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main id="creacion-stands" className="stands-page">
      <section className="stands-hero" style={{ backgroundImage: `linear-gradient(90deg, rgba(4, 24, 52, .9) 0%, rgba(8, 49, 98, .65) 48%, rgba(8, 49, 98, .1) 100%), url("${asset(projects[1].folder, projects[1].images[0])}")` }}>
        <Container>
          <div className="stands-hero-copy">
            <p>{t('standsPage.hero.eyebrow')}</p>
            <h1>{t('standsPage.hero.title')}</h1>
            <span>{t('standsPage.hero.description')}</span>
            <Link to="#stands-contact" className="stands-primary-link">{t('standsPage.hero.link')} <FaArrowRight aria-hidden="true" /></Link>
          </div>
        </Container>
      </section>

      <section className="stands-projects">
        <Container>
          <div className="stands-section-heading">
            <p>{t('standsPage.projects.eyebrow')}</p>
            <h2>{t('standsPage.projects.title')}</h2>
            <span>{t('standsPage.projects.subtitle')}</span>
          </div>
          <div className="stands-project-selector">
            {projects.map((item, index) => (
              <button type="button" className={index === selectedProject ? 'is-selected' : ''} onClick={() => setSelectedProject(index)} key={item.client}>
                <img src={asset(item.folder, item.images[0])} alt="" loading="lazy" />
                <span>{item.client}</span>
              </button>
            ))}
          </div>
          <article className="stands-project-detail">
            <div className="stands-project-copy">
              <p>{t(`standsPage.projects.items.${project.categoryKey}.category`)}</p>
              <h3>{project.client}</h3>
              <span>{t(`standsPage.projects.items.${project.categoryKey}.description`)}</span>
              <h4>{t('standsPage.projects.servicesIncluded')}</h4>
              <ul>
                {project.serviceKeys.map((service) => <li key={service}><FaCheck aria-hidden="true" /> {t(`standsPage.projects.services.${service}`)}</li>)}
              </ul>
            </div>
            <div className="stands-project-gallery">
              {project.images.map((image) => <img key={image} src={asset(project.folder, image)} alt={project.client + ' — proyecto'} loading="lazy" />)}
            </div>
          </article>
        </Container>
      </section>

      <section className="stands-types">
        <Container>
          <div className="stands-section-heading">
            <p>{t('standsPage.types.eyebrow')}</p>
            <h2>{t('standsPage.types.title')}</h2>
            <span>{t('standsPage.types.subtitle')}</span>
          </div>
          <div className="stands-types-grid">
            {standTypes.map((stand) => (
              <article className="stands-type-card" key={stand.key}>
                <img src={stand.image} alt={t(`standsPage.types.items.${stand.key}.title`)} loading="lazy" />
                <div>
                  <h3>{t(`standsPage.types.items.${stand.key}.title`)}</h3>
                  <p>{t(`standsPage.types.items.${stand.key}.description`)}</p>
                  <dl>
                    <div><dt>{t('standsPage.types.differential')}</dt><dd>{t(`standsPage.types.items.${stand.key}.differential`)}</dd></div>
                    <div><dt>{t('standsPage.types.applications')}</dt><dd>{t(`standsPage.types.items.${stand.key}.applications`)}</dd></div>
                  </dl>
                </div>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section className="stands-trust">
        <Container>
          <div className="stands-trust-grid">
            {trustPoints.map(({ icon: Icon, key }) => (
              <article key={key}>
                <Icon aria-hidden="true" />
                <strong>{t(`standsPage.trust.stats.${key}.value`)}</strong>
                <span>{t(`standsPage.trust.stats.${key}.label`)}</span>
              </article>
            ))}
          </div>
          <div className="stands-trust-copy">
            <h2>{t('standsPage.trust.title')}</h2>
            <p>{t('standsPage.trust.paragraph1')}</p>
            <p>{t('standsPage.trust.paragraph2')}</p>
          </div>
        </Container>
      </section>

      <section id="stands-contact" className="stands-contact">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <div className="stands-section-heading">
                <p>{t('standsPage.form.eyebrow')}</p>
                <h2>{t('standsPage.form.title')}</h2>
                <span>{t('standsPage.form.subtitle')}</span>
              </div>
              <Form onSubmit={handleSubmit} className="stands-contact-form">
                <Row>
                  <Col md={6} className="mb-3"><Form.Group><Form.Label>{t('contactForm.name')} *</Form.Label><Form.Control type="text" name="name" value={formData.name} onChange={handleChange} required /></Form.Group></Col>
                  <Col md={6} className="mb-3"><Form.Group><Form.Label>{t('contactForm.email')} *</Form.Label><Form.Control type="email" name="email" value={formData.email} onChange={handleChange} required /></Form.Group></Col>
                </Row>
                <Row>
                  <Col md={6} className="mb-3"><Form.Group><Form.Label>{t('contactForm.phone')}</Form.Label><Form.Control type="tel" name="phone" value={formData.phone} onChange={handleChange} /></Form.Group></Col>
                  <Col md={6} className="mb-3"><Form.Group><Form.Label>{t('standsPage.form.eventName')}</Form.Label><Form.Control type="text" name="eventName" value={formData.eventName} onChange={handleChange} placeholder={t('standsPage.form.eventPlaceholder')} /></Form.Group></Col>
                </Row>
                <Form.Group className="mb-4"><Form.Label>{t('contactForm.message')} *</Form.Label><Form.Control as="textarea" rows={5} name="message" value={formData.message} onChange={handleChange} placeholder={t('standsPage.form.messagePlaceholder')} required /></Form.Group>
                <div className="d-grid"><Button className="stands-submit-button" type="submit" size="lg" disabled={isSubmitting}>{isSubmitting ? t('standsPage.form.submitting') : t('standsPage.form.submit')}</Button></div>
              </Form>
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
};

export default CreacionStands;
